import {ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation,} from "@angular/core";
import {CommonModule} from "@angular/common";
import {CKEditorModule} from "@ckeditor/ckeditor5-angular";

import {
  AccessibilityHelp,
  AutoLink,
  Autosave,
  Bold,
  ClassicEditor,
  type EditorConfig,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize, Italic,
  Link,
  List,
  Paragraph,
  SelectAll, Underline,
  Undo,
} from "ckeditor5";
import {Timestamp} from "./plugins/timestamp";
import {CustomPluginAction} from "./plugins/customPlugin";
import {QundisGpt} from "./plugins/qundisGpt";
import {MarkForTechnician} from "./plugins/markForTechnician";
import {FormsModule} from "@angular/forms";
import {AiService} from "../../../features/admin/data-access/ai.service";

const DEFAULT_TOOLBAR_ITEMS = [
  "undo",
  "redo",
  "|",
  "bold",
  "italic",
  "underline",
  "|",
  "fontSize",
  "fontColor",
  "fontBackgroundColor",
  "|",
  "bulletedList",
  "numberedList",
  "|"
]

export type EditorPlugin = 'QundisGPT'

// | 'MarkForTechnician' | 'Timestamp'

@Component({
  selector: "app-editor",
  standalone: true,
  imports: [CommonModule, CKEditorModule, FormsModule],
  templateUrl: "./editor.component.html",
  styleUrls: ["./editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: "app-editor",
  }
})
export class EditorComponent {
  content = input<string>('');
  placeholder = input<string>('');
  plugins = input<EditorPlugin[]>([]);
  newEditorEvent = output<string>();
  gptResponse = output<string>();

  constructor(private aiService: AiService) {
    QundisGpt.aiService = this.aiService
  }

  protected editorData = signal<string>('');
  public editorInstance = signal<any>(null);

  Editor = ClassicEditor;

  config = computed<EditorConfig>(() => ({
    licenseKey: 'GPL',
    toolbar: {
      items: [
        ...DEFAULT_TOOLBAR_ITEMS,
        ...(this.plugins()?.map(name => name) ?? [])
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AccessibilityHelp,
      AutoLink,
      Autosave,
      Bold,
      Italic,
      Underline,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      Link,
      List,
      Paragraph,
      SelectAll,
      Undo,
      Timestamp,
      QundisGpt,
      CustomPluginAction,
      MarkForTechnician,
    ],
    fontFamily: {
      supportAllValues: true,
    },
    fontSize: {
      options: [10, 12, 14, "default", 18, 20, 22],
      supportAllValues: true,
    },
    // initialData: this.content() || this.placeholder(),
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: "https://",
      decorators: {
        toggleDownloadable: {
          mode: "manual",
          label: "Downloadable",
          attributes: {
            download: "file",
          },
        },
      },
    },
    placeholder: this.placeholder(),
  }))

  onReady(editor: any) {
    this.editorInstance.set(editor);

    const initialContent = this.content() || '';
    if (initialContent) {
      editor.setData(initialContent);
    }

    editor.on('qundisGptResponse', (event: any, response: string) => {
      this.gptResponse.emit(response);
    })
  }

  onChange(event: { editor: { getData: () => string } }) {
    const data = event.editor.getData();
    this.editorData.set(data);
    this.newEditorEvent.emit(data);
  }
}
